/* ZemanatReleaseList.js — ابطال یا تقلیل ضمانتنامه در قبال صورت وضعیت
   همتای «استرداد سپرده در قبال صورت وضعیت»، اما به جای ریز سپرده،
   ضمانتنامه‌ها به صورت وضعیت تخصیص می‌گیرند (tblZemanat.ReleaseDepositOrZemanatId).

   چیدمان:
     صفحه         → فهرست صورت وضعیت‌ها
     پاپ‌آپ ۱      → ضمانتنامه‌های همان پیمانکار/قرارداد
     پاپ‌آپ ۲      → ابطال یا تقلیل یک ضمانتنامه — قابل جابجایی با درگ
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';

/* ── کمکی‌ها ── */
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const APIZ   = (path,opts={}) => fetch(`/api/zemanat${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(r=>r.json());
const APID   = (path,opts={}) => fetch(`/api/deposit${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(r=>r.json());

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

const FSelect = ({label,name,value,onChange,options,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value??''} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',
        width:'100%',boxSizing:'border-box',background:'#fff'}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const FDate = ({label,value,onChange,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <JalaliDatePicker value={value} onChange={onChange} placeholder={label}/>
  </div>
);

const INIT_WIDTHS = [45,150,220,110,110,150,150,150,150,150,110];

export default function ZemanatReleaseList({ user, onBack }) {
  /* ── جدول اصلی: صورت وضعیت‌ها ── */
  const [rows,setRows]         = useState([]);
  const [total,setTotal]       = useState(0);
  const [hasMore,setHasMore]   = useState(false);
  const [loading,setLoading]   = useState(false);
  const [selected,setSelected] = useState(null);

  const [contractor,setContractor]   = useState(0);
  const [contractors,setContractors] = useState([]);
  const [ready,setReady] = useState(false);

  /* ── پاپ‌آپ ۱: ضمانتنامه‌های قابل ابطال/تقلیل ── */
  const [zemOpen,setZemOpen]   = useState(false);
  const [zemRows,setZemRows]   = useState([]);
  const [zemSums,setZemSums]   = useState({sum_amount:0,sum_allocated:0});
  const [zemBy,setZemBy]       = useState('contractor');
  const [zemLoading,setZemLoading] = useState(false);

  /* ── پاپ‌آپ ۲: ابطال یا تقلیل — قابل جابجایی ── */
  const [act,setAct]         = useState(null);   // ضمانتنامه در حال عملیات
  const [actKind,setActKind] = useState('cancel');
  const [actForm,setActForm] = useState({});
  const [actError,setActError] = useState('');
  const [pos,setPos] = useState({x:0,y:0});
  const dragRef = useRef({on:false,dx:0,dy:0});

  const [toast,setToast] = useState('');
  const offsetRef = useRef(0);

  const {TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['contract_no','contractor','invoice_no','contract_subject'],
    serverTotal:total, serverHasMore:hasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* درگ پاپ‌آپ ۲ */
  useEffect(()=>{
    const move = e => {
      if(!dragRef.current.on) return;
      setPos({x:e.clientX-dragRef.current.dx, y:e.clientY-dragRef.current.dy});
    };
    const up = ()=>{ dragRef.current.on=false; };
    window.addEventListener('mousemove',move);
    window.addEventListener('mouseup',up);
    return ()=>{ window.removeEventListener('mousemove',move); window.removeEventListener('mouseup',up); };
  },[]);
  const startDrag = e => {
    dragRef.current = {on:true, dx:e.clientX-pos.x, dy:e.clientY-pos.y};
    e.preventDefault();
  };

  /* صورت وضعیت‌ها از همان اندپوینت بخش سپرده می‌آیند */
  useEffect(()=>{ (async()=>{
    try{
      const d = await APID('/releases/init/');
      if(d.ok) setContractors(d.contractors||[]);
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await APID(`/releases/?offset=${off}&contractor_code=${contractor}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setHasMore(d.has_more);
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[contractor]);

  useEffect(()=>{ if(ready) fetchData(true); },[ready,contractor]); // eslint-disable-line

  /* ── پاپ‌آپ ضمانتنامه‌ها ── */
  const loadZemanats = useCallback(async(rel,by)=>{
    if(!rel) return;
    setZemLoading(true);
    const d = await APIZ(`/releases/${rel.id}/zemanats/?by=${by}`);
    if(d.ok){
      setZemRows(d.rows);
      setZemSums({sum_amount:d.sum_amount,sum_allocated:d.sum_allocated});
    } else alert(d.error||'خطا');
    setZemLoading(false);
  },[]);

  const openZemPopup = async r => {
    setSelected(r);
    const by = r.release_kind===1?'contract':'contractor';
    setZemBy(by); setZemOpen(true);
    await loadZemanats(r,by);
  };
  useEffect(()=>{ if(zemOpen && selected) loadZemanats(selected,zemBy); },[zemBy]); // eslint-disable-line

  /* ── پاپ‌آپ عملیات ── */
  const openAct = row => {
    if(row.is_given){ alert('این ضمانتنامه ضبط شده و قابل ابطال یا تقلیل نیست'); return; }
    if(row.assigned_other){ alert('این ضمانتنامه به صورت وضعیت دیگری متصل است'); return; }
    setAct(row);
    setActKind(row.assigned_here ? 'detach' : 'cancel');
    setActForm({cancel_no:row.cancel_no||'', cancel_date:row.cancel_date||'',
      cancel_bank_no:'', cancel_bank_date:'',
      new_amount:'', new_end_date:'', receive_no:'', receive_date:'',
      send_no:'', send_date:'', remark:''});
    setActError('');
    setPos({x:-170,y:120});
  };

  const saveAct = async()=>{
    if(actKind==='cancel'){
      const cn=String(actForm.cancel_no||'').trim(), cd=String(actForm.cancel_date||'').trim();
      if(!!cn !== !!cd){ setActError('شماره یا تاریخ ابطال کامل نیست'); return; }
      if(!cn){ setActError('شماره و تاریخ ابطال لازم است'); return; }
    }
    if(actKind==='decrease'){
      const na = Number(actForm.new_amount||0);
      if(na===0){ setActError('مبلغ جدید ضمانتنامه وارد نشده است'); return; }
      if(na>=act.current_amount){
        setActError('مبلغ تقلیل باید کمتر از مبلغ ضمانتنامه باشد'); return; }
    }
    setActError('');
    const d = await APIZ(`/releases/${selected.id}/zemanats/${act.id}/assign/`,
      {method:'POST',body:JSON.stringify({action:actKind,...actForm})});
    if(d.ok){
      setAct(null);
      await loadZemanats(selected,zemBy);
      showToast(actKind==='detach'?'✅ اتصال برداشته شد'
        :actKind==='cancel'?'✅ ابطال ثبت شد':'✅ تقلیل ثبت شد');
    } else setActError(d.error||'خطا');
  };

  const contractorOpts = [{value:0,label:'همه پیمانکاران'},
    ...contractors.map(c=>({value:c.code,label:c.title}))];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>ابطال یا تقلیل ضمانتنامه در قبال صورت وضعیت</div>
               <div style={s.hSub}>بخش ضمانتنامه</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            <button onClick={()=>selected&&openZemPopup(selected)} disabled={!selected}
              style={{...s.btnZem,opacity:selected?1:.4}}>📜 ضمانتنامه‌های این صورت وضعیت</button>
            <button onClick={()=>fetchData(true)} style={s.btnRefresh}>↻ بازخوانی</button>
          </div>
          <span style={{marginRight:'auto',fontSize:'11px',color:'#888'}}>
            صورت وضعیت‌ها در «استرداد سپرده در قبال صورت وضعیت» ثبت و ویرایش می‌شوند.
          </span>
        </div>

        <div style={s.toolbar2}>
          <div style={{width:'300px'}}>
            <FSelect label="پیمانکار" name="contractor" value={contractor}
              onChange={e=>setContractor(Number(e.target.value))} options={contractorOpts}/>
          </div>
          <div style={{marginRight:'auto'}}>
            <div style={s.fL}>جستجو</div>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="قرارداد / پیمانکار / صورت وضعیت" style={{...s.fI,width:'300px'}}/>
          </div>
        </div>

        {/* ── جدول اصلی ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>قرارداد</TH>
                <TH idx={2} right>موضوع قرارداد</TH>
                <TH idx={3}>تاریخ</TH>
                <TH idx={4}>صورت وضعیت</TH>
                <TH idx={5} right>پیمانکار</TH>
                <TH idx={6} gold>مبلغ ناخالص</TH>
                <TH idx={7} red>کسورات</TH>
                <TH idx={8} gold>خالص</TH>
                <TH idx={9} green>پرداختی از ضمانتنامه</TH>
                <TH idx={10}>تاریخ درخواست</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={11} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6' : idx%2===0 ? '#fff' : '#fdf6e3';
                return (
                  <tr key={r.id} onClick={()=>setSelected(r)} onDoubleClick={()=>openZemPopup(r)}
                    style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={s.td}>{toFa(r.contract_no)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contract_subject}>{r.contract_subject}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.contract_date)}</td>
                    <td style={s.td}>{r.invoice_kind} {toFa(r.invoice_no)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contractor}>{r.contractor}</td>
                    <td style={{...s.td,...s.num}}>{numFmt(r.invoice_amount)}</td>
                    <td style={{...s.td,...s.num,color:'#c62828'}}>{numFmt(r.deduction)}</td>
                    <td style={{...s.td,...s.num,color:'#6a1b9a'}}>{numFmt(r.net)}</td>
                    <td style={{...s.td,...s.num,color:'#1565c0'}}>{numFmt(r.pay_zemanat)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.request_date)}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          {selected && <>
            <span>انتخابی: <b>{selected.contractor}</b> — صورت وضعیت
              {' '}<b>{selected.invoice_kind} {toFa(selected.invoice_no)}</b></span>
            <span>خالص: <b style={{color:'#6a1b9a'}}>{numFmt(selected.net)||'۰'}</b></span>
          </>}
          <span style={{marginRight:'auto',color:'#888'}}>دابل‌کلیک روی ردیف = ضمانتنامه‌ها</span>
        </div>
      </main>

      {/* ══ پاپ‌آپ ۱: ضمانتنامه‌ها ══ */}
      {zemOpen && selected && (
        <div style={s.overlay} onClick={()=>{if(!act)setZemOpen(false);}}>
          <div style={{...s.modal,maxWidth:'1150px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#0d2137,#1a2f50)'}}>
              <div style={s.mTitle}>
                📜 ضمانتنامه‌ها — {selected.contractor} | صورت وضعیت
                {' '}{selected.invoice_kind} {toFa(selected.invoice_no)}
                {' '}| مبلغ {numFmt(selected.invoice_amount)}
              </div>
              <button onClick={()=>{setAct(null);setZemOpen(false);}} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              <div style={{display:'flex',gap:'16px',alignItems:'center',marginBottom:'10px',flexWrap:'wrap'}}>
                <label style={s.radio}>
                  <input type="radio" checked={zemBy==='contractor'} onChange={()=>setZemBy('contractor')}/>
                  <span>پیمانکار (همه قراردادها)</span></label>
                <label style={s.radio}>
                  <input type="radio" checked={zemBy==='contract'} onChange={()=>setZemBy('contract')}/>
                  <span>فقط این قرارداد</span></label>
                <span style={{marginRight:'auto',fontSize:'12px',color:'#555'}}>
                  جمع ضمانتنامه‌ها: <b>{numFmt(zemSums.sum_amount)||'۰'}</b> |
                  {' '}تخصیص این صورت وضعیت: <b style={{color:'#1565c0'}}>
                    {numFmt(zemSums.sum_allocated)||'۰'}</b>
                </span>
              </div>
              <div style={{overflow:'auto',maxHeight:'58vh'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>
                    {['شماره','سپام','نوع تضمین','موضوع تضمین','قرارداد','مبلغ اولیه','مبلغ جاری',
                      'صدور','سررسید','ابطال','وضعیت','']
                      .map(h=><th key={h} style={s.subTh}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {zemLoading&&<tr><td colSpan={12} style={s.empty}>در حال بارگذاری...</td></tr>}
                    {!zemLoading&&zemRows.length===0&&
                      <tr><td colSpan={12} style={s.empty}>ضمانتنامه‌ای برای این پیمانکار نیست</td></tr>}
                    {zemRows.map((r,idx)=>(
                      <tr key={r.id} onDoubleClick={()=>openAct(r)}
                        style={{cursor:'pointer',
                          background:r.assigned_here?'#e3f2fd'
                            :r.assigned_other?'#eceff1'
                            :r.is_given?'#ffebee'
                            :idx%2===0?'#fff':'#fffde7'}}>
                        <td style={{...s.subTd,direction:'ltr',fontWeight:'700'}}>{toFa(r.zemanat_no)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.sepam)}</td>
                        <td style={s.subTd}>{r.kind_title}</td>
                        <td style={s.subTd}>{r.subject_title}</td>
                        <td style={s.subTd}>{toFa(r.contract_no)}</td>
                        <td style={{...s.subTd,...s.num,color:'#1b5e20'}}>{numFmt(r.original_amount)}</td>
                        <td style={{...s.subTd,...s.num,
                          color:r.current_amount!==r.original_amount?'#e65100':'#999'}}>
                          {numFmt(r.current_amount)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.issu_date)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.end_date)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.cancel_date)}</td>
                        <td style={s.subTd}>
                          {r.is_given ? <span style={{color:'#c62828',fontWeight:'700'}}>ضبط شده</span>
                           :r.assigned_here ? <span style={{color:'#1565c0',fontWeight:'700'}}>این صورت وضعیت</span>
                           :r.assigned_other ? <span style={{color:'#607d8b'}}>صورت وضعیت دیگر</span>
                           :r.cancel_no ? <span style={{color:'#607d8b'}}>باطل شده</span>
                           : <span style={{color:'#1b5e20',fontWeight:'700'}}>معتبر</span>}
                        </td>
                        <td style={s.subTd}>
                          {r.is_given||r.assigned_other
                            ? <span style={{color:'#c62828'}}>🚫</span>
                            : <button onClick={()=>openAct(r)} style={s.btnMini}>
                                {r.assigned_here?'تغییر':'ابطال/تقلیل'}</button>}
                        </td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize:'11px',color:'#888',marginTop:'8px'}}>
                دابل‌کلیک روی ردیف یا دکمه = پنجره ابطال/تقلیل. آن پنجره قابل جابجایی است.
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{setAct(null);setZemOpen(false);}} style={s.btnCancel}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ پاپ‌آپ ۲: ابطال یا تقلیل — قابل جابجایی ══ */}
      {act && (
        <div style={{...s.floatWrap,transform:`translate(${pos.x}px, ${pos.y}px)`}}>
          <div style={s.floatBox}>
            <div style={s.floatHeader} onMouseDown={startDrag}>
              <span style={{fontSize:'13px',fontWeight:'700',color:'#fff'}}>
                📜 ضمانتنامه {toFa(act.zemanat_no)} — {act.kind_title}
              </span>
              <span style={{fontSize:'11px',color:'rgba(255,255,255,0.6)'}}>⠿ برای جابجایی بکشید</span>
              <button onClick={()=>setAct(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={{padding:'16px'}}>
              {actError&&<div style={s.errBox}>{actError}</div>}
              <div style={s.actInfo}>
                <div>مبلغ اولیه: <b>{numFmt(act.original_amount)}</b></div>
                <div>مبلغ جاری: <b style={{color:'#e65100'}}>{numFmt(act.current_amount)}</b></div>
                <div>سررسید: <b>{toFa(act.end_date)||'—'}</b></div>
                <div>وضعیت: <b>{act.assigned_here?'متصل به این صورت وضعیت':act.cancel_no?'باطل شده':'معتبر'}</b></div>
              </div>

              <div style={{display:'flex',gap:'14px',alignItems:'center',margin:'12px 0',flexWrap:'wrap'}}>
                <label style={s.radio}>
                  <input type="radio" checked={actKind==='cancel'} onChange={()=>setActKind('cancel')}/>
                  <span>ابطال کامل</span></label>
                <label style={s.radio}>
                  <input type="radio" checked={actKind==='decrease'} onChange={()=>setActKind('decrease')}/>
                  <span>تقلیل</span></label>
                {act.assigned_here && <label style={s.radio}>
                  <input type="radio" checked={actKind==='detach'} onChange={()=>setActKind('detach')}/>
                  <span style={{color:'#c62828'}}>برداشتن اتصال</span></label>}
              </div>

              {actKind==='cancel' && <>
                <div style={s.row}>
                  <FInput label="شماره ابطال *" name="cancel_no" value={actForm.cancel_no}
                    onChange={e=>setActForm(p=>({...p,cancel_no:e.target.value}))} flex="0 0 48%"/>
                  <FDate label="تاریخ ابطال *" value={actForm.cancel_date}
                    onChange={v=>setActForm(p=>({...p,cancel_date:v}))} flex="0 0 48%"/>
                </div>
                <div style={s.row}>
                  <FInput label="نامه ابطال بانک: شماره" name="cancel_bank_no"
                    value={actForm.cancel_bank_no}
                    onChange={e=>setActForm(p=>({...p,cancel_bank_no:e.target.value}))} flex="0 0 48%"/>
                  <FDate label="تاریخ نامه بانک" value={actForm.cancel_bank_date}
                    onChange={v=>setActForm(p=>({...p,cancel_bank_date:v}))} flex="0 0 48%"/>
                </div>
              </>}

              {actKind==='decrease' && <>
                <div style={s.row}>
                  <FInput label="مبلغ جدید *" name="new_amount" value={actForm.new_amount}
                    onChange={e=>setActForm(p=>({...p,new_amount:e.target.value}))}
                    type="number" dir="ltr" flex="0 0 48%"/>
                  <FDate label="تاریخ اعتبار جدید" value={actForm.new_end_date}
                    onChange={v=>setActForm(p=>({...p,new_end_date:v}))} flex="0 0 48%"/>
                </div>
                <div style={s.row}>
                  <FInput label="نامه دریافتی: شماره" name="receive_no" value={actForm.receive_no}
                    onChange={e=>setActForm(p=>({...p,receive_no:e.target.value}))} flex="0 0 48%"/>
                  <FDate label="تاریخ دریافت" value={actForm.receive_date}
                    onChange={v=>setActForm(p=>({...p,receive_date:v}))} flex="0 0 48%"/>
                </div>
                <div style={s.row}>
                  <FInput label="توضیح" name="remark" value={actForm.remark}
                    onChange={e=>setActForm(p=>({...p,remark:e.target.value}))}/>
                </div>
                {selected && <button
                  onClick={()=>setActForm(p=>({...p,
                    new_amount:Math.max(act.current_amount-(selected.net||0),0)}))}
                  style={s.btnMini}>کسر خالص صورت وضعیت از مبلغ</button>}
              </>}

              {actKind==='detach' && (
                <div style={s.warnBox}>
                  اتصال این ضمانتنامه به صورت وضعیت برداشته می‌شود و شماره و تاریخ ابطال آن
                  پاک می‌گردد. رکوردهای تقلیل ثبت‌شده حذف نمی‌شوند.
                </div>)}

              <div style={{...s.infoBox,marginTop:'10px'}}>
                ابطال، شماره و تاریخ ابطال ضمانتنامه را پر می‌کند و آن را به این صورت وضعیت
                متصل می‌سازد. تقلیل یک رکورد در «تمدیدها و تقلیل‌ها» ثبت می‌کند.
              </div>
            </div>
            <div style={{...s.mFooter,borderRadius:'0 0 14px 14px'}}>
              <button onClick={()=>setAct(null)} style={s.btnCancel}>لغو</button>
              <button onClick={saveAct}
                style={{...s.btnSave,background:actKind==='detach'
                  ?'linear-gradient(135deg,#b71c1c,#c62828)'
                  :'linear-gradient(135deg,#1b5e20,#2e7d32)'}}>
                {actKind==='detach'?'برداشتن اتصال':'ثبت'}</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'flex-end',gap:'12px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px',flexWrap:'wrap'},
  radio:{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  btnZem:{padding:'7px 16px',background:'linear-gradient(135deg,#4a148c,#6a1b9a)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnRefresh:{padding:'7px 16px',background:'linear-gradient(135deg,#37474f,#455a64)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnMini:{padding:'4px 10px',background:'#e3f2fd',color:'#0d47a1',border:'1px solid #90caf9',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontSize:'11px',fontWeight:'700'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'18px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subTh:{padding:'7px 9px',background:'#0d2137',color:'#C8A84B',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'760px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'14px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  warnBox:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'9px 14px',color:'#e65100',marginBottom:'8px',fontSize:'12px',lineHeight:'1.9'},
  infoBox:{background:'#e3f2fd',border:'1px solid #90caf9',borderRadius:'8px',padding:'9px 14px',color:'#0d47a1',fontSize:'11px',lineHeight:'1.9'},
  actInfo:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 14px',fontSize:'12px',color:'#444',background:'#fafafa',border:'1px solid #eee',borderRadius:'8px',padding:'10px 14px'},
  floatWrap:{position:'fixed',top:'50%',left:'50%',marginTop:'-230px',marginLeft:'-280px',zIndex:1200},
  floatBox:{width:'560px',background:'#fff',borderRadius:'14px',boxShadow:'0 24px 70px rgba(0,0,0,0.5)',border:'2px solid #1a2f50',direction:'rtl'},
  floatHeader:{display:'flex',alignItems:'center',gap:'10px',justifyContent:'space-between',padding:'10px 14px',background:'linear-gradient(135deg,#4a148c,#6a1b9a)',borderRadius:'12px 12px 0 0',cursor:'move',userSelect:'none'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};
